import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import {
  dismissPersistent, isDismissedPersistent, clearDismissedPersistent,
} from '../lib/dismiss-registry.js';

/**
 * HelpTour — lightweight coachmark tour anchored to elements via data-tour attributes.
 *
 * Usage:
 *   1. Wrap your app in <HelpTourProvider>
 *   2. Add data-tour="some-id" to any element you want to anchor a tip to
 *   3. The tour definition below maps id → step
 *   4. ? icon in the TopBar triggers it
 *   5. localStorage persists "dismissed" so it doesn't show again
 */

const STORAGE_KEY = 'netcom_connect_tour_dismissed';

// Per-route tours — each is a sequence of steps anchored to elements
const TOURS = {
  '/': [
    { anchor: 'kpi-strip',     title: 'Your fleet at a glance',         body: 'These four KPIs roll up every terminal on your account — uptime, active lines, data used, and average latency.' },
    { anchor: 'service-lines', title: 'Service lines',                  body: 'Click any line to drill in for plan details, terminal assignment, historical usage, and audit history.' },
    { anchor: 'terminals',     title: 'Terminals (dishes)',             body: 'Click into a terminal for live telemetry, outage history, and a health checklist.' },
    { anchor: 'alerts-feed',   title: 'Open alerts',                    body: 'New alerts surface here so your NOC can triage without leaving the dashboard.' },
    { anchor: 'quick-actions', title: 'Quick actions',                  body: 'Common workflows — submitting a service request, generating a report, configuring alert rules.' },
  ],
  '/terminals': [
    { anchor: 'terminals-table', title: 'Terminals table',              body: 'Multi-select rows to run bulk operations — reboot several at once, export to CSV.' },
  ],
  '/network': [
    { anchor: 'network-map',     title: 'Network map',                  body: 'A real, zoomable satellite map — pan and zoom in on any site, click a pin to drill into that device.' },
  ],
};

const TourContext = createContext(null);

export function useHelpTour() {
  const ctx = useContext(TourContext);
  if (!ctx) throw new Error('useHelpTour must be inside HelpTourProvider');
  return ctx;
}

export function HelpTourProvider({ children }) {
  const location = useLocation();
  const [activeStep, setActiveStep] = useState(null); // { index, total, ... }
  const [autoTriggered, setAutoTriggered] = useState(new Set());

  const tour = TOURS[location.pathname] || null;

  // Auto-trigger tour on first visit to a page IF not dismissed.
  // Uses the shared dismiss-registry so HelpTour + alerts + banners
  // can all be reset via a single `clearDismissedPersistent('tour:')`.
  useEffect(() => {
    if (!tour) return;
    // Shareable demo links (?demo=) and screenshot captures suppress the
    // coachmark tour so it never overlays the first screen.
    try { if (new URLSearchParams(window.location.search).get('demo')) return; } catch { /* ignore */ }
    if (autoTriggered.has(location.pathname)) return;
    if (isDismissedPersistent(`tour:${location.pathname}`)) {
      setAutoTriggered((s) => new Set([...s, location.pathname]));
      return;
    }
    // Delay so the page renders first
    const timer = setTimeout(() => {
      start();
      setAutoTriggered((s) => new Set([...s, location.pathname]));
    }, 600);
    return () => clearTimeout(timer);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const start = useCallback(() => {
    if (!tour || tour.length === 0) return;
    setActiveStep({ index: 0 });
  }, [tour]);

  const dismiss = useCallback((persist = true) => {
    setActiveStep(null);
    if (persist) {
      dismissPersistent(`tour:${location.pathname}`, { at: new Date().toISOString() });
    }
  }, [location.pathname]);

  const next = useCallback(() => {
    if (!tour) return;
    setActiveStep((s) => {
      if (!s) return null;
      if (s.index + 1 >= tour.length) {
        dismiss(true);
        return null;
      }
      return { index: s.index + 1 };
    });
  }, [tour, dismiss]);

  const prev = useCallback(() => {
    setActiveStep((s) => s && s.index > 0 ? { index: s.index - 1 } : s);
  }, []);

  const replayAll = useCallback(() => {
    // Clear every dismissed tour AND the legacy key so previously-
    // shipped builds also reset.
    clearDismissedPersistent('tour:');
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* ignore */ }
    setAutoTriggered(new Set());
    start();
  }, [start]);

  const value = { start, dismiss, next, prev, replayAll, activeStep, tour, hasTour: !!tour };

  return (
    <TourContext.Provider value={value}>
      {children}
      {activeStep !== null && tour && <TourPopover step={tour[activeStep.index]} stepNum={activeStep.index} total={tour.length} onNext={next} onPrev={prev} onSkip={() => dismiss(true)} />}
    </TourContext.Provider>
  );
}

function TourPopover({ step, stepNum, total, onNext, onPrev, onSkip }) {
  const [pos, setPos] = useState(null);

  useEffect(() => {
    function compute() {
      const el = document.querySelector(`[data-tour="${step.anchor}"]`);
      if (!el) { setPos({ centered: true }); return; }
      const r = el.getBoundingClientRect();
      // documentElement.clientWidth is the real layout-viewport width (what
      // CSS actually renders against); window.innerWidth can disagree with
      // it in some embedded/scaled contexts, which throws this clamp off.
      const viewportWidth = document.documentElement.clientWidth;
      // Position below the element by default. On narrow screens the
      // popover itself shrinks (see w-80 → max-w-[calc(100vw-2rem)] below),
      // so clamp against that real width, not a hardcoded 320px — otherwise
      // the clamp math assumes more room than the popover actually has and
      // the card (esp. its Next button) hangs off the right edge.
      const tipWidth = Math.min(320, viewportWidth - 32), tipHeight = 200;
      let top = r.bottom + window.scrollY + 12;
      let left = r.left + window.scrollX + r.width / 2 - tipWidth / 2;
      // Keep within viewport
      if (left + tipWidth > viewportWidth - 16) left = viewportWidth - tipWidth - 16;
      if (left < 16) left = 16;
      // If popover would go off bottom, flip above the element
      if (top + tipHeight > window.scrollY + window.innerHeight) {
        top = r.top + window.scrollY - tipHeight - 12;
      }
      setPos({
        top, left,
        targetRect: { top: r.top + window.scrollY, left: r.left + window.scrollX, width: r.width, height: r.height },
      });
      // Scroll the target into view
      el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
    compute();
    const timer = setTimeout(compute, 300); // re-measure after scroll
    window.addEventListener('resize', compute);
    return () => { clearTimeout(timer); window.removeEventListener('resize', compute); };
  }, [step]);

  if (!pos) return null;

  const last = stepNum === total - 1;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 pointer-events-none" style={{ background: 'rgba(15, 23, 42, 0.35)' }} />

      {/* Spotlight cutout */}
      {pos.targetRect && !pos.centered && (
        <div
          className="fixed z-40 rounded-lg pointer-events-none"
          style={{
            top: pos.targetRect.top - 6 - window.scrollY,
            left: pos.targetRect.left - 6,
            width: pos.targetRect.width + 12,
            height: pos.targetRect.height + 12,
            boxShadow: '0 0 0 9999px rgba(15, 23, 42, 0.35), 0 0 0 3px rgba(47, 195, 229, 0.8)',
            background: 'transparent',
            transition: 'all 0.25s ease',
          }}
        />
      )}

      {/* Popover — capped to the viewport width (minus margin) so it can
          never hang off-screen on a phone-width layout. */}
      <div
        className="fixed z-50 w-80 max-w-[calc(100vw-2rem)] bg-white rounded-xl border border-[var(--color-line-1)] shadow-2xl p-4"
        style={pos.centered ? { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' } : { top: pos.top - window.scrollY, left: pos.left }}
      >
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-wider text-[var(--color-brand-600)] font-semibold">
            Tip {stepNum + 1} of {total}
          </div>
          <button onClick={onSkip} aria-label="Close tour" className="text-[var(--color-ink-4)] hover:text-[var(--color-ink-1)] -mt-1 -mr-1 p-1">
            <svg className="w-4 h-4" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"><path d="m4 4 8 8M12 4l-8 8" strokeLinecap="round" /></svg>
          </button>
        </div>
        <h3 className="font-semibold text-[var(--color-ink-1)] text-base mb-1.5">{step.title}</h3>
        <p className="text-sm text-[var(--color-ink-3)]">{step.body}</p>
        <div className="mt-4 pt-3 border-t border-[var(--color-line-soft)] flex items-center justify-between">
          <button onClick={onSkip} className="text-xs font-medium text-[var(--color-ink-4)] hover:text-[var(--color-ink-1)]">Skip tour</button>
          <div className="flex items-center gap-1.5">
            {stepNum > 0 && (
              <button onClick={onPrev} className="text-xs font-medium text-[var(--color-ink-2)] border border-[var(--color-line-1)] hover:border-[var(--color-brand-500)] px-2.5 py-1 rounded">
                ← Back
              </button>
            )}
            <button onClick={onNext} className="bg-[var(--color-brand-500)] hover:bg-[var(--color-brand-600)] text-white text-xs font-semibold px-3 py-1 rounded">
              {last ? 'Done' : 'Next →'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
